import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ConfiguratorApiService } from '../core/api/configurator-api.service';
import { I18nService } from '../core/i18n/i18n.service';
import { TranslatePipe } from '../core/i18n/translate.pipe';

@Component({
  selector: 'app-pricing-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './pricing-admin-page.component.html',
  styleUrl: './shared-page.css'
})
export class PricingAdminPageComponent {
  private readonly api = inject(ConfiguratorApiService);
  private readonly i18n = inject(I18nService);

  pricebookName = 'Standard-Preisbuch';
  pricebookCurrency = 'EUR';
  pricebookId = '';
  priceRuleType: 'BASE_PRICE' | 'OPTION_DELTA' | 'CONDITIONAL_ADJUSTMENT' = 'OPTION_DELTA';
  priceRulePriority = 100;
  priceRuleConditionType: 'ALWAYS' | 'OPTION_SELECTED' = 'OPTION_SELECTED';
  priceRuleConditionOptionCode = 'SUNROOF';
  priceRuleAmount = '1200.00';
  priceRuleCode = 'OPT_SUNROOF';
  priceRuleReason = 'Schiebedach-Option';

  busy = false;
  error = '';
  info = '';

  createPricebook(): void {
    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.createPricebook({ name: this.pricebookName.trim(), currency: this.pricebookCurrency.trim() })
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: (pricebookId) => {
          this.pricebookId = pricebookId;
          this.info = this.i18n.t('pricing.info.pricebookCreated', { id: pricebookId });
        },
        error: (err) => this.error = this.extractError(err)
      });
  }

  addPriceRule(): void {
    if (!this.pricebookId.trim()) {
      this.error = this.i18n.t('pricing.error.pricebookIdRequired');
      return;
    }

    const condition = this.priceRuleConditionType === 'ALWAYS'
      ? { type: 'ALWAYS' }
      : { type: 'OPTION_SELECTED', optionCode: this.priceRuleConditionOptionCode.trim() };

    const action = {
      code: this.priceRuleCode.trim(),
      reason: this.priceRuleReason.trim(),
      amount: this.priceRuleAmount.trim()
    };

    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.createPriceRule(this.pricebookId.trim(), {
      ruleType: this.priceRuleType,
      priority: this.priceRulePriority,
      condition,
      action
    }).pipe(finalize(() => this.busy = false)).subscribe({
      next: (priceRuleId) => this.info = this.i18n.t('pricing.info.priceRuleCreated', { id: priceRuleId }),
      error: (err) => this.error = this.extractError(err)
    });
  }

  publishPricebook(): void {
    if (!this.pricebookId.trim()) {
      this.error = this.i18n.t('pricing.error.pricebookIdRequired');
      return;
    }
    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.publishPricebook(this.pricebookId.trim())
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: () => this.info = this.i18n.t('pricing.info.pricebookPublished'),
        error: (err) => this.error = this.extractError(err)
      });
  }

  private extractError(err: unknown): string {
    if (typeof err === 'object' && err !== null && 'error' in err) {
      const payload = (err as { error?: { error?: string } }).error;
      if (payload?.error) {
        return payload.error;
      }
    }
    return this.i18n.t('common.requestFailedJwt');
  }
}
